import React, { useState } from 'react';
import { Input, Tabs, Dropdown } from 'antd';
import type { MenuProps } from 'antd';
import { Search, ChevronDown } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { AssessmentStatus } from '@/services/api';
import { HtsSubmissionSummaryStatus } from '@evaltick/shared';
import { useSessionRoutes, useIsGuest } from '@/hooks';
import { ROUTES, buildRoute } from '@/routes';
import { HistoryItemKind, type HistoryItem } from './historyItem';
import type { PartKey } from './interviewStatus';
import styles from './PastSessionsList.module.scss';

const PAGE_SIZE = 5;

interface PastSessionsListProps {
  history: HistoryItem[];
  loadingHistory: boolean;
  onLoadIntoCard: (templateId: string, part: PartKey, mode: 'retry' | 'resume') => void;
  onResumeAssessment: (assessmentId: string) => void;
  resumingAssessmentId: string | null;
}

type RowStatus = 'prog' | 'done';

interface Row {
  key: string;
  id: string;
  templateId: string;
  part: PartKey;
  name: string;
  isHts: boolean;
  /** The template was revoked — the row stays readable, but there's nothing left to retry. */
  revoked: boolean;
  status: RowStatus;
  date: string;
  totalQuestions: number;
}

const formatDate = (iso: string): string =>
  new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });

const buildRows = (history: HistoryItem[]): Row[] =>
  history.map((entry) => {
    if (entry.kind === HistoryItemKind.Assessment) {
      const { item } = entry;
      return {
        key: entry.key,
        id: item.id,
        templateId: item.interviewTemplate.id,
        part: 'comp' as const,
        name: item.interviewTemplate.name,
        isHts: false,
        revoked: item.interviewTemplate.revokedAt !== null,
        status: item.status === AssessmentStatus.Completed ? 'done' : 'prog',
        date: entry.date,
        totalQuestions: item.interviewTemplate.questionCount,
      };
    }
    const { item } = entry;
    return {
      key: entry.key,
      id: item.id,
      templateId: item.interviewTemplateId,
      part: 'hts' as const,
      name: item.interviewTemplateName,
      isHts: true,
      revoked: item.interviewTemplateRevokedAt !== null,
      status: item.status === HtsSubmissionSummaryStatus.Draft ? 'prog' : 'done',
      date: entry.date,
      totalQuestions: item.interviewTemplateQuestionCount,
    };
  });

interface DisplayItem {
  templateId: string;
  name: string;
  rows: Row[];
  sortKey: number;
}

const buildDisplayItems = (filtered: Row[]): DisplayItem[] => {
  const byTemplate = new Map<string, Row[]>();
  filtered.forEach((r) => {
    const existing = byTemplate.get(r.templateId);
    if (existing) existing.push(r);
    else byTemplate.set(r.templateId, [r]);
  });

  const grouped = new Set<string>();
  const items: DisplayItem[] = [];

  byTemplate.forEach((rowsForTemplate) => {
    const latestOf = (isHts: boolean) =>
      rowsForTemplate
        .filter((r) => r.isHts === isHts)
        .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())[0];
    const latestHts = latestOf(true);
    const latestComp = latestOf(false);
    if (latestHts && latestComp) {
      grouped.add(latestHts.key);
      grouped.add(latestComp.key);
      items.push({
        templateId: latestComp.templateId,
        name: latestComp.name,
        rows: [latestComp, latestHts],
        sortKey: Math.max(new Date(latestHts.date).getTime(), new Date(latestComp.date).getTime()),
      });
    }
  });

  filtered.forEach((r) => {
    if (grouped.has(r.key)) return;
    items.push({ templateId: r.templateId, name: r.name, rows: [r], sortKey: new Date(r.date).getTime() });
  });

  return items.sort((a, b) => b.sortKey - a.sortKey);
};

export const PastSessionsList: React.FC<PastSessionsListProps> = ({
  history,
  loadingHistory,
  onLoadIntoCard,
  onResumeAssessment,
  resumingAssessmentId,
}) => {
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<'all' | 'prog' | 'done'>('all');
  const [kindFilter, setKindFilter] = useState<'all' | 'comp' | 'hts'>('all');
  const [limit, setLimit] = useState(PAGE_SIZE);
  const navigate = useNavigate();
  const sessionRoutes = useSessionRoutes();
  // A guest gets one sitting per access code, so "Try again" has nothing to
  // start for them.
  const isGuest = useIsGuest();

  const goToResults = (row: Row) => {
    navigate(row.isHts ? buildRoute(ROUTES.HTS_READINESS, { submissionId: row.id }) : sessionRoutes.results(row.id));
  };

  const renderRowBody = (row: Row) => (
    <>
      <div className={styles.pt}>
        <span className={styles.ptName}>
          <span className={row.isHts ? styles.ghost : styles.compBadge}>
            {row.isHts ? 'HTS' : 'Competency'}
          </span>
          <span
            className={
              row.status === 'done'
                ? `${styles.statusTag} ${styles.statusTagDone}`
                : `${styles.statusTag} ${styles.statusTagProg}`
            }
          >
            {row.status === 'done' ? 'Completed' : 'In progress'}
          </span>
        </span>
        <span className={styles.ptMeta}>
          {formatDate(row.date)}
          {` · ${row.totalQuestions} questions`}
        </span>
      </div>
      <div className={styles.pacts}>
        {row.status === 'done' ? (
          <>
            <button type="button" className={styles.lnk} onClick={() => goToResults(row)}>
              See results
            </button>
            {!isGuest && !row.revoked && (
              <button
                type="button"
                className={`${styles.lnk} ${styles.lnkSolid}`}
                onClick={() => onLoadIntoCard(row.templateId, row.part, 'retry')}
              >
                Try again
              </button>
            )}
          </>
        ) : row.isHts ? (
          <button
            type="button"
            className={`${styles.lnk} ${styles.lnkSolid}`}
            onClick={() => onLoadIntoCard(row.templateId, row.part, 'resume')}
          >
            Resume
          </button>
        ) : (
          <button
            type="button"
            className={`${styles.lnk} ${styles.lnkSolid}`}
            onClick={() => onResumeAssessment(row.id)}
            disabled={resumingAssessmentId === row.id}
          >
            {resumingAssessmentId === row.id ? 'Resuming…' : 'Resume'}
          </button>
        )}
      </div>
    </>
  );

  if (!loadingHistory && history.length === 0) {
    return (
      <div className={styles.emptyRow}>Past sessions will appear here.</div>
    );
  }

  const rows = buildRows(history);
  const countOf = (status: RowStatus) => rows.filter((r) => r.status === status).length;
  const countOfKind = (isHts: boolean) => rows.filter((r) => r.isHts === isHts).length;

  const trimmedSearch = search.trim();
  const filtered = rows.filter((r) => {
    if (trimmedSearch) return r.name.toLowerCase().includes(trimmedSearch.toLowerCase());
    if (filter !== 'all' && r.status !== filter) return false;
    if (kindFilter !== 'all' && r.isHts !== (kindFilter === 'hts')) return false;
    return true;
  });

  const updateSearch = (value: string) => {
    setSearch(value);
    setLimit(PAGE_SIZE);
  };
  const updateFilter = (value: 'all' | 'prog' | 'done') => {
    setFilter(value);
    setLimit(PAGE_SIZE);
  };
  const updateKindFilter = (value: 'all' | 'comp' | 'hts') => {
    setKindFilter(value);
    setLimit(PAGE_SIZE);
  };

  const displayItems = buildDisplayItems(filtered);
  const visible = displayItems.slice(0, limit);

  return (
    <div className={styles.pastCard}>
      <div className={styles.pastHead}>
        <label className={styles.find}>
          <Search className={styles.findIcon} style={{ width: '1em', height: '1em' }} />
          <Input
            value={search}
            onChange={(e) => updateSearch(e.target.value)}
            placeholder="Search past sessions"
            aria-label="Search past sessions"
            className={styles.findInput}
            variant="borderless"
          />
        </label>
        <div className={styles.filterRow}>
          <Tabs
            activeKey={filter}
            onChange={(key) => updateFilter(key as 'all' | 'prog' | 'done')}
            className={styles.statusTabs}
            items={[
              { key: 'all', label: `All ${rows.length}` },
              { key: 'prog', label: `In progress ${countOf('prog')}` },
              { key: 'done', label: `Completed ${countOf('done')}` },
            ]}
          />
          <Dropdown
            trigger={['click']}
            menu={{
              selectedKeys: [kindFilter],
              onClick: ({ key }) => updateKindFilter(key as 'all' | 'comp' | 'hts'),
              items: [
                { key: 'all', label: `All types ${rows.length}` },
                { key: 'comp', label: `Competency ${countOfKind(false)}` },
                { key: 'hts', label: `HTS ${countOfKind(true)}` },
              ] satisfies MenuProps['items'],
            }}
          >
            <button type="button" className={styles.kindDropdownTrigger}>
              {kindFilter === 'all' ? 'All types' : kindFilter === 'comp' ? 'Competency' : 'HTS'}
              <ChevronDown className={styles.kindDropdownIcon} style={{ width: '1em', height: '1em' }} />
            </button>
          </Dropdown>
        </div>
      </div>

      {visible.length === 0 ? (
        <p className={styles.memp}>
          {trimmedSearch ? `Nothing matches "${trimmedSearch}".` : 'Nothing in this filter.'}
        </p>
      ) : (
        visible.map((item) => (
          <div className={styles.groupBlock} key={item.rows[0].key}>
            <div className={styles.groupTitle}>{item.name}</div>
            <div className={styles.groupBody}>
              {item.rows.map((row) => (
                <div className={styles.subRow} key={row.key}>
                  {renderRowBody(row)}
                </div>
              ))}
            </div>
          </div>
        ))
      )}

      {displayItems.length > limit && (
        <button type="button" className={styles.showMore} onClick={() => setLimit((n) => n + PAGE_SIZE)}>
          Show more ({displayItems.length - limit} left)
        </button>
      )}
    </div>
  );
};

export default PastSessionsList;
